// src/components/layout/Layout.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../Header';
import Footer from '../Footer';
import Sidebar from './Sidebar';
import { ThemeProvider, useTheme } from '../../context/ThemeContext';

function LayoutContent({ children }) {
  const [username, setUsername] = useState('');
  const [activeTab, setActiveTab] = useState('overview');
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [deviceType, setDeviceType] = useState('desktop');
  const navigate = useNavigate();
  const { currentTheme } = useTheme();

  useEffect(() => {
    const checkDeviceType = () => {
      const width = window.innerWidth;
      if (width <= 480) {
        setDeviceType('mobile');
        setIsSidebarOpen(false); // Auto-close on mobile
      } else if (width <= 768) {
        setDeviceType('tablet');
        setIsSidebarOpen(false); // Auto-close on tablet
      } else if (width <= 1024) {
        setDeviceType('laptop');
        // Keep sidebar closed by default on laptop
        setIsSidebarOpen(false);
      } else {
        setDeviceType('desktop');
        // Keep sidebar closed by default on desktop
        setIsSidebarOpen(false);
      }
    };

    checkDeviceType();
    window.addEventListener('resize', checkDeviceType);
    return () => window.removeEventListener('resize', checkDeviceType);
  }, []);

  useEffect(() => {
    const storedUsername = sessionStorage.getItem('username');
    if (storedUsername) {
      setUsername(storedUsername);
    }
  }, []);

  const handleLogout = () => {
    sessionStorage.removeItem('isAuth');
    sessionStorage.removeItem('username');
    sessionStorage.removeItem('fullName');
    sessionStorage.removeItem('userId');
    sessionStorage.removeItem('userRole');
    navigate('/');
  };

  const handleToggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  const handleNavigation = (path) => {
    navigate(path);
    setActiveTab(path.substring(1));
    
    // Auto-close sidebar on mobile/tablet after navigation
    if (deviceType === 'mobile' || deviceType === 'tablet') {
      setIsSidebarOpen(false);
    }
  };

  // Calculate main content styles based on device
  const getMainContentStyle = () => {
    if (deviceType === 'mobile' || deviceType === 'tablet') {
      return {
        marginTop: deviceType === 'mobile' ? '60px' : '56px',
        width: '100%',
        transition: 'all 0.3s ease',
        position: 'relative',
        zIndex: 1,
        paddingBottom: '60px' // Add space for footer
      };
    }

    // Desktop/Laptop styles
    const sidebarWidth = isSidebarOpen ? 
      (deviceType === 'laptop' ? '280px' : '280px') : '70px';
    
    return {
      marginLeft: sidebarWidth,
      marginTop: '56px',
      width: `calc(100% - ${sidebarWidth})`,
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      position: 'relative',
      zIndex: 1,
      paddingBottom: '60px' // Add space for footer
    };
  };

  // Calculate sidebar position based on device
  const getHeaderHeight = () => {
    switch(deviceType) {
      case 'mobile':
        return '60px';
      case 'tablet':
      case 'laptop':
      case 'desktop':
      default:
        return '56px';
    }
  };

  // Adjust container padding based on device
  const getContainerPadding = () => {
    switch(deviceType) {
      case 'mobile':
        return '0.75rem';
      case 'tablet':
        return '1rem';
      case 'laptop':
        return '1.25rem';
      default:
        return '1.5rem';
    }
  };

  return (
    <div 
      className="d-flex flex-column min-vh-100 position-relative"
      style={{
        '--primary-color': currentTheme.primary,
        '--secondary-color': currentTheme.secondary,
        '--text-color': currentTheme.text,
        backgroundColor: '#f8f9fa'
      }}
    >
      <Header 
        username={username} 
        onLogout={handleLogout}
        onToggleSidebar={handleToggleSidebar}
        isSidebarOpen={isSidebarOpen}
        deviceType={deviceType}
      />
      
      <div className="d-flex flex-grow-1 position-relative">
        <Sidebar 
          activeTab={activeTab} 
          onNavigation={handleNavigation}
          isSidebarOpen={isSidebarOpen}
          onToggleSidebar={handleToggleSidebar}
        />
        
        <main 
          className="flex-grow-1 bg-light overflow-auto"
          style={getMainContentStyle()}
        >
          <div 
            className="container-fluid"
            style={{
              padding: getContainerPadding(),
              minHeight: `calc(100vh - ${getHeaderHeight()} - 60px)`
            }}
          >
            {children}
          </div>
        </main>

        {/* Mobile/Tablet overlay when sidebar is open */}
        {(deviceType === 'mobile' || deviceType === 'tablet') && isSidebarOpen && (
          <div 
            className="position-fixed top-0 start-0 w-100 h-100 bg-dark bg-opacity-50"
            style={{ 
              zIndex: 999,
              animation: 'fadeIn 0.3s ease'
            }}
            onClick={handleToggleSidebar}
          />
        )}
      </div>

      <Footer deviceType={deviceType} />
    </div>
  );
}

function Layout({ children }) {
  return (
    <ThemeProvider>
      <LayoutContent>{children}</LayoutContent>
    </ThemeProvider>
  );
}

export default Layout;